import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  CalendarDays, Search, ShoppingCart, CreditCard, PartyPopper,
  CheckCircle2, ArrowRight, Clock, Users, Star, Shield
} from 'lucide-react';

const STEPS = [
  {
    number: '01',
    icon: CalendarDays,
    title: 'Tell Us About Your Event',
    description: 'Start by entering your event details - date, time, guest count, and location. This helps us show you services available in your area.',
    color: 'from-blue-500 to-cyan-500'
  },
  {
    number: '02',
    icon: Search,
    title: 'Browse & Customize Services',
    description: 'Explore our curated selection of catering, entertainment, staffing, and more. Customize each service with your preferred options and add-ons.',
    color: 'from-indigo-500 to-purple-500'
  },
  {
    number: '03',
    icon: ShoppingCart,
    title: 'Build Your Event Package',
    description: 'Add services to your cart and see your instant quote update in real-time. Adjust quantities, guest counts, and service hours as needed.',
    color: 'from-purple-500 to-pink-500'
  },
  {
    number: '04',
    icon: CreditCard,
    title: 'Review & Book',
    description: 'Review your complete event package, then book instantly with secure payment. You\'ll receive a confirmation email with all the details.',
    color: 'from-pink-500 to-red-500'
  },
  {
    number: '05',
    icon: PartyPopper,
    title: 'Enjoy Your Event',
    description: 'Sit back and relax! Our vetted professionals will arrive on time and deliver exceptional service. Focus on enjoying your event.',
    color: 'from-amber-500 to-orange-500'
  }
];

const FEATURES = [
  {
    icon: Clock,
    title: 'Instant Quotes',
    description: 'No waiting for callbacks. Get accurate pricing immediately.'
  },
  {
    icon: Users,
    title: 'Vetted Professionals',
    description: 'Every vendor and staff member is carefully screened.'
  },
  {
    icon: Star,
    title: 'Quality Guaranteed',
    description: 'We stand behind every service we provide.'
  },
  {
    icon: Shield,
    title: 'Secure Booking',
    description: 'Your payment and data are protected with bank-level security.'
  }
];

export default function HowItWorksPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background pt-20 pb-16">
      {/* Hero */}
      <div className="bg-gradient-to-b from-slate-900 to-slate-800 text-foreground py-16 -mt-20 pt-36">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">How It Works</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Planning your perfect event is simple with Eventism. Here's what to expect when you use our platform.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Steps */}
        <div className="py-16">
          <div className="space-y-12">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const isEven = idx % 2 === 1;
              
              return (
                <div 
                  key={step.number} 
                  className={`flex flex-col md:flex-row items-center gap-8 ${isEven ? 'md:flex-row-reverse' : ''}`}
                >
                  {/* Icon */}
                  <div className="flex-shrink-0">
                    <div className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}>
                      <Icon className="h-12 w-12 text-foreground" />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className={`flex-1 text-center md:text-left ${isEven ? 'md:text-right' : ''}`}>
                    <span className="text-5xl font-bold text-muted-foreground/20">{step.number}</span>
                    <h3 className="text-2xl font-bold mt-2 mb-3">{step.title}</h3>
                    <p className="text-muted-foreground text-lg">{step.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Features Grid */}
        <div className="py-12 border-t">
          <h2 className="text-2xl font-bold text-center mb-8">Why Choose Eventism?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <Card key={idx} className="text-center">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-6 w-6 text-indigo-500" />
                    </div>
                    <h3 className="font-semibold mb-2">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* What's Included */}
        <div className="py-12 border-t">
          <h2 className="text-2xl font-bold text-center mb-8">What's Included With Every Booking</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              'Instant booking confirmation via email',
              'Detailed invoice with all service information',
              'Direct communication with your service providers',
              'Flexible modifications up until your event date',
              'Dedicated support team available by phone and email',
              'Quality guarantee on all services',
              'Secure payment processing',
              'Post-event support if needed'
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 rounded-lg bg-muted/50">
                <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="py-12">
          <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-foreground border-0">
            <CardContent className="py-12 text-center">
              <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
              <p className="text-foreground/80 mb-8 max-w-xl mx-auto">
                Join thousands of happy customers who have planned their events with Eventism. Get your instant quote today!
              </p>
              <Button 
                size="lg"
                className="bg-white text-indigo-600 hover:bg-white/90"
                onClick={() => navigate('/plan-event')}
              >
                Start Planning Your Event <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
